import type { BaseEntity, SimpleTableConfig } from "../../UniversalCRMTable/types/shared";
import type { LinkedCompanyAccount, ParentEntity } from "@/contexts/DetailContextProvider.types";
import type { DetailActionContext } from "../../UniversalDetailPage/types/detailPage";

export interface DetailModalContentProps<T extends BaseEntity> {
  itemId: string;
  item: T | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  modalId: string;
  entityName: string;
  config: SimpleTableConfig<T>;
  actionContext: DetailActionContext<T> | null;
  detailActions: any[];
  entityTabsConfig: any;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  headerTitle: string;
  headerSubtitle: string;
  canNavigate: boolean | null;
  canGoPrevious: boolean | null;
  canGoNext: boolean | null;
  handlePrevious: () => void;
  handleNext: () => void;
  handleInlineEdit: (item: T) => Promise<void>;
  handleEdit: (item: T) => void;
  handleDelete: (item: T) => Promise<void>;
  parentEntity: ParentEntity | undefined;
  linkedCompanyAccount?: LinkedCompanyAccount | null;
}

/**
 * Custom comparison for DetailModalContent memoization. Only re-render on
 * critical prop changes (itemId, activeTab, isLoading, error, tags). The
 * `item` reference is intentionally ignored because it churns on refetch
 * after inline edits while displayed data is memoized via entityData.
 */
export function areDetailModalContentPropsEqual<T extends BaseEntity>(
  prevProps: DetailModalContentProps<T>,
  nextProps: DetailModalContentProps<T>
): boolean {
  if (prevProps.isLoading !== nextProps.isLoading) {
    if (!nextProps.item && nextProps.isLoading) {
      return false;
    }
  }

  if (prevProps.error !== nextProps.error) return false;
  if (prevProps.itemId !== nextProps.itemId) return false;
  if (prevProps.activeTab !== nextProps.activeTab) return false;
  if (prevProps.modalId !== nextProps.modalId) return false;
  if (prevProps.entityName !== nextProps.entityName) return false;
  if (prevProps.config !== nextProps.config) return false;
  if (prevProps.actionContext !== nextProps.actionContext) return false;
  if (prevProps.detailActions !== nextProps.detailActions) return false;
  if (prevProps.entityTabsConfig !== nextProps.entityTabsConfig) return false;
  if (
    prevProps.headerTitle !== nextProps.headerTitle ||
    prevProps.headerSubtitle !== nextProps.headerSubtitle
  )
    return false;
  if (
    prevProps.canNavigate !== nextProps.canNavigate ||
    prevProps.canGoPrevious !== nextProps.canGoPrevious ||
    prevProps.canGoNext !== nextProps.canGoNext
  )
    return false;
  if (JSON.stringify(prevProps.parentEntity) !== JSON.stringify(nextProps.parentEntity))
    return false;
  if (
    JSON.stringify(prevProps.linkedCompanyAccount ?? null) !==
    JSON.stringify(nextProps.linkedCompanyAccount ?? null)
  )
    return false;

  const prevTags = prevProps.item?.tags;
  const nextTags = nextProps.item?.tags;
  if (JSON.stringify(prevTags) !== JSON.stringify(nextTags)) return false;

  return true;
}
